import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Layers3, Compass, CalendarRange } from 'lucide-react';

interface PrismeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'ideologie' | 'cycle' | 'presentation' | 'budget-ti';

export default function PrismeModal({ isOpen, onClose }: PrismeModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('ideologie');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-950 z-50"
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 16 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white text-slate-900 border border-slate-200 shadow-2xl rounded-2xl w-[92vw] h-[92vh] flex flex-col overflow-hidden">
          <div className="border-b border-slate-200 px-6 py-5 bg-white">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center shadow-lg">
                  <Layers3 className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Idéologie PRIsME</h2>
                  <p className="text-sm text-slate-600">
                    Une approche structurée pour aligner les TI avec les objectifs d’affaires du client, sans pression de vente.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white p-2 text-slate-700 hover:bg-slate-100 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="px-6 pt-4 pb-3 border-b border-slate-200 bg-white">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('ideologie')}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                  activeTab === 'ideologie'
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-700 border border-slate-300'
                }`}
              >
                Idéologie PRIsME
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('cycle')}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                  activeTab === 'cycle'
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-700 border border-slate-300'
                }`}
              >
                Cycle d’accompagnement
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('presentation')}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                  activeTab === 'presentation'
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-700 border border-slate-300'
                }`}
              >
                Présentation PRIsME
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('budget-ti')}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                  activeTab === 'budget-ti'
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-700 border border-slate-300'
                }`}
              >
                Budget TI recommandé
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 bg-white space-y-6">
            {activeTab === 'ideologie' && (
              <>
                <section className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Compass className="w-5 h-5 text-slate-700" />
                    <h3 className="text-xl font-bold text-slate-900">Idéologie PRIsME</h3>
                  </div>
                  <p className="text-sm text-slate-600">
                    Une approche structurée pour aligner les TI avec les objectifs d’affaires du client, sans pression de vente.
                  </p>
                </section>

                <div className="grid xl:grid-cols-2 gap-4">
                  <Card title="Positionnement">
                    <BulletList items={[
                      'PRIsME est une démarche de validation stratégique.',
                      'L’objectif n’est pas de vendre une solution immédiatement.',
                      'L’objectif est de comprendre la réalité du client, ses priorités, ses contraintes et ses objectifs d’affaires.',
                      'L’approche permet de sécuriser les décisions TI avant d’investir.',
                    ]} />
                  </Card>

                  <Card title="Approche">
                    <BulletList items={[
                      'Méthode rigoureuse et standardisée.',
                      'Alignement des solutions TI avec les besoins d’affaires.',
                      'Prise en compte des contraintes budgétaires et temporelles.',
                      'Vision technologique, humaine et processus.',
                    ]} />
                  </Card>

                  <Card title="Valeur pour le client">
                    <BulletList items={[
                      'Clarifier les priorités.',
                      'Réduire les risques de mauvaises décisions.',
                      'Améliorer la gouvernance TI.',
                      'Démontrer visuellement la valeur ajoutée et le ROI.',
                      'Identifier les opportunités de transformation numérique, d’automatisation et d’IA.',
                    ]} />
                  </Card>

                  <Card title="Éléments discutés en rencontre #1">
                    <BulletList items={[
                      'Présentation de la démarche PRIsME.',
                      'Explication des rencontres du pré-cycle.',
                      'Présentation de l’ébauche de gouvernance TI.',
                      'Présentation de l’ébauche du cadre référentiel.',
                      'Discussion sur le budget annuel du client.',
                      'Discussion sur les subventions disponibles.',
                      'Identification d’un contact pour l’inventaire des actifs TI.',
                    ]} />
                  </Card>
                </div>

                <Card title="PRIsME-Client">
                  <BulletList items={[
                    'Visualisation de la maturité actuelle.',
                    'Définition de la cible souhaitée.',
                    'Suivi de la progression.',
                    'Gestion documentaire.',
                    'Suivi du comité TI.',
                    'Suivi des projets.',
                    'Visualisation des activités stratégiques, tactiques et opérationnelles.',
                    'Mesure de certaines performances : tickets, processus, activités client.',
                    'Assistance virtuelle.',
                    'Solutions spécifiques selon les besoins du client.',
                  ]} />
                </Card>
              </>
            )}

            {activeTab === 'cycle' && (
              <>
                <section className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarRange className="w-5 h-5 text-slate-700" />
                    <h3 className="text-xl font-bold text-slate-900">Cycle d’accompagnement client PRIsME / Pré-cycle</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">
                    Un accompagnement structuré en 5 rencontres pour passer de la compréhension des besoins à un plan d’action clair.
                  </p>
                  <p className="text-sm text-slate-700">
                    Le pré-cycle PRIsME permet de cadrer les besoins du client, d’identifier les priorités, de valider les contraintes, puis de produire les livrables nécessaires à la planification TI.
                  </p>
                </section>

                <Card title="Pré-cycle">
                  <BulletList items={[
                    'Céduler les 5 rencontres avec le client.',
                    'Idéalement, les rencontres 1 et 2 sont planifiées rapprochées ou dans la même période.',
                    'Rencontre 3 : maximum 5 jours après la première rencontre.',
                    'Rencontre 4 : maximum 7 jours après la rencontre 3.',
                    'Rencontre 5 : maximum 5 jours après la rencontre 4.',
                  ]} />
                </Card>

                <div className="grid xl:grid-cols-2 gap-4">
                  <StepCard
                    title="Rencontre #1 — Alignement stratégique"
                    clientTime="2 h"
                    prepTime="4 h"
                    objectifs={[
                      'Présenter ou rappeler l’idéologie PRIsME.',
                      'Présenter une ébauche de gouvernance TI à finaliser ou réviser.',
                      'Présenter une ébauche de cadre référentiel à finaliser ou réviser.',
                      'Donner au client un devoir de réflexion sur ses besoins et priorités.',
                    ]}
                  />

                  <StepCard
                    title="Rencontre #2 — Inventaire et prise de besoin"
                    clientTime="2 h"
                    prepTime="4 h"
                    objectifs={[
                      'Présenter le catalogue des actifs à valider.',
                      'Réaliser une prise de besoin holistique, incluant les besoins d’affaires.',
                      'Prioriser les besoins sans contrainte de temps ou d’argent.',
                      'Donner au client un devoir de réflexion sur ses contraintes de temps et d’argent.',
                    ]}
                  />

                  <StepCard
                    title="Rencontre #3 — Priorisation avec contraintes"
                    clientTime="2 h"
                    prepTime="5 h"
                    objectifs={[
                      'Présenter une ébauche ou mise à jour du document des besoins avec les coûts associés.',
                      'Prioriser les besoins selon les contraintes temporelles et budgétaires du client.',
                      'Faire réviser les besoins, contraintes de temps et contraintes budgétaires.',
                      'Préparer le client aux ébauches qui seront présentées à la rencontre 4.',
                    ]}
                  />

                  <StepCard
                    title="Rencontre #4 — Planification"
                    clientTime="2 h"
                    prepTime="15 h"
                    objectifs={[
                      'Présenter le document final des besoins.',
                      'Présenter l’ébauche ou mise à jour du plan annuel.',
                      'Présenter l’ébauche ou mise à jour du plan stratégique 3 à 5 ans.',
                      'Présenter l’ébauche ou mise à jour de l’offre de service BZ.',
                    ]}
                  />

                  <StepCard
                    title="Rencontre #5 — Livraison finale"
                    clientTime="2 h"
                    prepTime="4 h"
                    objectifs={[
                      'Présenter le plan annuel en version finale.',
                      'Présenter le plan stratégique en version finale.',
                      'Présenter l’offre de service en version finale.',
                    ]}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <SummaryCard title="Temps client total" value="10 h" />
                  <SummaryCard title="Temps de préparation total" value="32 h" />
                </div>
              </>
            )}

            {activeTab === 'presentation' && (
              <>
                <section className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Layers3 className="w-5 h-5 text-slate-700" />
                    <h3 className="text-xl font-bold text-slate-900">Présentation PRIsME</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">
                    Synthèse intégrée du PowerPoint « Guide des rencontres du client durant le pré-cycle pour les Directeurs TI ».
                  </p>
                  <p className="text-sm text-slate-700">
                    Cette vue reprend la logique des rencontres client, les messages clés de la démarche PRIsME et les principaux livrables à présenter selon l’avancement du pré-cycle.
                  </p>
                </section>

                <div className="grid md:grid-cols-3 gap-4">
                  <SummaryCard title="Rencontres client" value="5" />
                  <SummaryCard title="Fenêtre recommandée" value="6 semaines" />
                  <SummaryCard title="Livrables clés" value="4 docs + 1 tableau" />
                </div>

                <Card title="Positionnement présenté au client">
                  <BulletList items={[
                    'Votre Directeur TI devient le point de contact principal chez BZ.',
                    'Le rôle est celui d’un conseiller TI en solution d’affaires, sans pression de vente.',
                    'Le Directeur TI agit comme acteur impartial dans le comité TI existant ou à mettre en place.',
                    'L’objectif est d’optimiser le budget TI selon une stratégie de 5 ans.',
                    'La gouvernance commence par une matrice RACI claire entre les acteurs internes et externes.',
                  ]} />
                </Card>

                <div className="grid xl:grid-cols-2 gap-4">
                  <Card title="Le cadre référentiel et ses piliers">
                    <BulletList items={[
                      'Mesurer la maturité actuelle de l’entreprise.',
                      'Définir la maturité cible souhaitée.',
                      'Planifier et prioriser les activités pour faire progresser les piliers choisis.',
                      'Ajouter un suivi rigoureux des activités tactiques et stratégiques, au-delà des opérations.',
                      'Démontrer visuellement la progression, la valeur ajoutée, le ROI et l’adéquation solutions/besoins.',
                    ]} />
                  </Card>

                  <Card title="Transformation numérique, IA et PRIsME-Client">
                    <BulletList items={[
                      'Réflexion sur les opportunités de transformation numérique.',
                      'Discussion sur l’automatisation de tâches/processus simples et répétitifs.',
                      'Vulgarisation des usages possibles de l’IA pour soutenir employés et décideurs.',
                      'Discussion préliminaire sur les subventions et crédits disponibles.',
                      'Présentation du portail PRIsME-Client : maturité, KPI, gouvernance, suivis, documentation, projets, catalogue des actifs et solutions spécifiques.',
                    ]} />
                  </Card>
                </div>

                <Card title="Livrables annoncés au client">
                  <BulletList items={[
                    'Capture holistique et priorisée des besoins d’affaires.',
                    'Tableau Besoins-Solutions-Coûts.',
                    'Plan annuel TI.',
                    'Plan stratégique TI 5 ans.',
                    'ODS.',
                  ]} />
                </Card>

                <div className="grid xl:grid-cols-2 gap-4">
                  <StepCard
                    title="Rencontre #1 — Introduction à PRIsME"
                    clientTime="2 h max"
                    prepTime="Préparation de présentation"
                    objectifs={[
                      'Présentation de l’idéologie PRIsME.',
                      'Présentation de la gouvernance, des rôles et responsabilités.',
                      'Présentation du cadre référentiel.',
                      'Présentation du tableau Besoins-Solutions-Coûts.',
                      'Présentation du portail PRIsME-Client.',
                    ]}
                  />

                  <StepCard
                    title="Rencontre #2 — Validation et prise de besoins"
                    clientTime="2 h max"
                    prepTime="Préparation du catalogue et des ébauches"
                    objectifs={[
                      'Discussion sur les besoins d’affaires.',
                      'Validation de la gouvernance TI.',
                      'Validation du cadre et de ses piliers.',
                      'Évaluation de la maturité actuelle et définition de la cible.',
                      'Présentation du catalogue des actifs à valider.',
                      'Prise de besoins holistique et priorisation sans contrainte.',
                    ]}
                  />

                  <StepCard
                    title="Rencontre #3 — Solutions et priorisation avec contraintes"
                    clientTime="2 h max"
                    prepTime="Ébauches besoins/solutions/coûts"
                    objectifs={[
                      'Présentation des objectifs SMART et des solutions compréhensives.',
                      'Validation du document des besoins holistiques.',
                      'Présentation du tableau des besoins-solutions avec coûts.',
                      'Priorisation avec contraintes temporelles et budgétaires du client.',
                    ]}
                  />

                  <StepCard
                    title="Rencontre #4 — Révision des documents"
                    clientTime="2 h max"
                    prepTime="Ébauches finales"
                    objectifs={[
                      'Approbation du document final des besoins holistiques.',
                      'Présentation de l’ébauche du plan annuel.',
                      'Présentation de l’ébauche du plan stratégique 5 ans.',
                      'Présentation de l’ébauche de l’offre de service BZ.',
                    ]}
                  />

                  <StepCard
                    title="Rencontre #5 — Approbation finale"
                    clientTime="2 h max"
                    prepTime="Versions finales"
                    objectifs={[
                      'Approbation finale du plan annuel.',
                      'Approbation finale du plan stratégique.',
                      'Approbation finale de l’offre de service.',
                    ]}
                  />

                  <Card title="Questions et devoirs à cadrer">
                    <BulletList items={[
                      'Les services TI actuels répondent-ils réellement aux attentes et besoins d’affaires?',
                      'Y a-t-il des besoins/objectifs actuels ou futurs nécessitant une intervention?',
                      'Le client a-t-il les budgets et le temps pour adresser ces défis?',
                      'Le client souhaite-t-il poursuivre la démarche et planifier les autres rencontres?',
                      'Devoirs : disponibilités, réflexion sur besoins/priorités/contraintes, et identification d’un contact pour l’inventaire des actifs TI.',
                    ]} />
                  </Card>
                </div>
              </>
            )}

            {activeTab === 'budget-ti' && (
              <>
                <section className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Compass className="w-5 h-5 text-slate-700" />
                    <h3 className="text-xl font-bold text-slate-900">Budget TI recommandé</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">
                    Quelle part du chiffre d’affaires une entreprise devrait-elle investir en technologies de l’information?
                  </p>
                  <p className="text-sm text-slate-700">
                    Il n’existe pas de pourcentage universel “magique”. En pratique, l’investissement TI varie selon le secteur, la maturité, les ambitions de croissance, la posture de sécurité et la dette technologique de l’organisation.
                  </p>
                </section>

                <div className="grid md:grid-cols-3 gap-4">
                  <SummaryCard title="PME classiques" value="2 % à 3 %" />
                  <SummaryCard title="Croissance / transformation" value="4 % à 6 %" />
                  <SummaryCard title="Tech / finance" value="7 % à 10 % +" />
                </div>

                <Card title="Lecture rapide par type d’entreprise">
                  <BulletList items={[
                    'PME classiques : environ 2 % à 3 % du chiffre d’affaires. Le budget sert surtout à maintenir les opérations : maintenance, sécurité de base, licences et support.',
                    'Entreprises en croissance ou en transformation : environ 4 % à 6 %. Le budget inclut la modernisation des systèmes, le Cloud, les ERP et l’optimisation des processus.',
                    'Secteurs technologiques ou financiers : de 7 % à plus de 10 %. La technologie y devient le produit lui-même ou le principal levier de service.',
                  ]} />
                </Card>

                <div className="grid xl:grid-cols-2 gap-4">
                  <Card title="Facteurs qui influencent le budget">
                    <BulletList items={[
                      'Secteur d’activité : la construction investit souvent moins (~1,5 %) que la banque ou l’assurance (>7 %).',
                      'Modèle de service : le passage au Cloud transforme des dépenses d’investissement (CapEx) en dépenses d’exploitation récurrentes (OpEx).',
                      'Niveau de sécurité : les entreprises manipulant des données sensibles doivent consacrer une part plus grande à la cybersécurité.',
                      'Dette technique : des systèmes vieillissants exigent parfois un rattrapage majeur à court terme.',
                    ]} />
                  </Card>

                  <Card title="Comment répartir l’investissement">
                    <BulletList items={[
                      'Exploitation (Run) — ~60 à 70 % : garder les lumières allumées, assurer support, serveurs, mises à jour et continuité.',
                      'Amélioration (Grow) — ~15 à 25 % : optimiser l’existant pour gagner en efficacité.',
                      'Innovation (Transform) — ~5 à 15 % : tester de nouvelles solutions créatrices d’avantage concurrentiel, comme l’IA ou les nouveaux services numériques.',
                    ]} />
                  </Card>
                </div>

                <div className="grid xl:grid-cols-3 gap-4">
                  <Card title="Run — Exploitation">
                    <p className="text-3xl font-bold text-cyan-300 mb-3">60–70 %</p>
                    <p>Support, serveurs, maintenance, mises à jour, sécurité de base, continuité.</p>
                  </Card>

                  <Card title="Grow — Amélioration">
                    <p className="text-3xl font-bold text-violet-300 mb-3">15–25 %</p>
                    <p>Optimisation des processus, amélioration des outils existants, gains d’efficacité.</p>
                  </Card>

                  <Card title="Transform — Innovation">
                    <p className="text-3xl font-bold text-emerald-300 mb-3">5–15 %</p>
                    <p>IA, automatisation, nouveaux services numériques, différenciation stratégique.</p>
                  </Card>
                </div>

                <div className="grid xl:grid-cols-2 gap-4">
                  <Card title="Indicateurs de performance à regarder">
                    <BulletList items={[
                      'Coût TI par employé : utile pour se comparer à des organisations de taille similaire.',
                      'Retour sur investissement (ROI) : un budget TI n’a de sens que s’il réduit les coûts, améliore la productivité ou soutient la croissance.',
                    ]} />
                  </Card>

                  <Card title="Message de vente / gouvernance recommandé">
                    <BulletList items={[
                      'Présenter l’investissement TI comme un levier de productivité, pas seulement comme un coût.',
                      'Relier le pourcentage investi aux objectifs d’affaires du client.',
                      'Expliquer que le bon niveau d’investissement dépend du niveau de risque, de croissance et de transformation visé.',
                      'Montrer que 5 % bien investi peut générer plus de valeur qu’un 2 % purement défensif.',
                    ]} />
                  </Card>
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-slate-900 border border-slate-700 rounded-xl p-4 space-y-3 shadow-lg">
      <h3 className="text-lg font-bold text-white">{title}</h3>
      <div className="text-sm text-slate-200 leading-relaxed">{children}</div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex gap-2 text-sm text-slate-200">
          <span className="text-cyan-300 mt-0.5">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function StepCard({ title, clientTime, prepTime, objectifs }: { title: string; clientTime: string; prepTime: string; objectifs: string[] }) {
  return (
    <section className="bg-slate-900 border border-slate-700 rounded-xl p-4 space-y-3 shadow-lg">
      <h3 className="text-lg font-bold text-white">{title}</h3>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="bg-slate-950 border border-slate-700 rounded-lg p-3">
          <p className="text-slate-400 text-xs uppercase tracking-wide">Durée client</p>
          <p className="font-semibold text-white">{clientTime}</p>
        </div>
        <div className="bg-slate-950 border border-slate-700 rounded-lg p-3">
          <p className="text-slate-400 text-xs uppercase tracking-wide">Préparation</p>
          <p className="font-semibold text-white">{prepTime}</p>
        </div>
      </div>
      <BulletList items={objectifs} />
    </section>
  );
}

function SummaryCard({ title, value }: { title: string; value: string }) {
  return (
    <section className="bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-lg">
      <p className="text-sm text-slate-400">{title}</p>
      <p className="text-2xl font-bold text-white mt-1">{value}</p>
    </section>
  );
}
